import React, { useState } from "react";

import {
  Activity,
  BadgeCheck,
  Bike,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Clock,
  Hash,
  Loader,
  MapPinned,
  Phone,
  Route,
  Search,
  ShieldAlert,
  ShoppingBag,
  Store,
  Truck,
  User,
} from "lucide-react";

import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";

import L from "leaflet";

import "leaflet/dist/leaflet.css";

import LoadingScreen from "../Utils/LoadingScreen";

// =====================================================
// LEAFLET MARKER CONFIG
// =====================================================

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),

  iconUrl: require("leaflet/dist/images/marker-icon.png"),

  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// =====================================================
// STATUS OPTIONS
// =====================================================

const statusOptions = [
  "unassigned",
  "assigned",
  "started",
  "inprogress",
  "successful",
  "food is ready",
  "acknowledged",
  "failed",
  "declined",
  "cancelled",
  "picked up",
  "arrived",
];

const TaskSearchTab = ({
  taskSearchWord,
  setTaskSearchWord,

  taskSearchResults,
  setTaskSearchResults,

  hasTaskSearched,
  setHasTaskSearched,

  taskDetailsLoading,
  handleTaskDetailsSearch,

  selectedSearchStatus,
  setSelectedSearchStatus,

  searchStatusUpdating,
  handleSearchStatusChange,

  searchLogsLoading,

  searchAssignmentLogs,

  searchStatusChangeLogs,
}) => {
  // =====================================================
  // SEARCH DATE STATE
  // =====================================================

const getTodayDate = () => {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const [fromDate, setFromDate] = useState(getTodayDate);
const [toDate, setToDate] = useState(getTodayDate);

  // =====================================================
  // FORMAT VALUE
  // =====================================================

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return value.toString();
  };

  const handleSearch = () => {
    handleTaskDetailsSearch({
      word: taskSearchWord.trim(),
      fromDate,
      toDate,
    });
  };

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDisplayDateTime = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // =====================================================
  // MINI INFO
  // =====================================================

  const MiniInfo = ({ icon: Icon, label, value }) => (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-1.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {Icon && <Icon size={11} />}

        {label}
      </div>

      <p className="break-words text-xs font-semibold leading-4 text-gray-900">
        {formatValue(value)}
      </p>
    </div>
  );

  // =====================================================
  // STATUS PILL
  // =====================================================

  const StatusPill = ({ status }) => {
    const value = status?.toLowerCase();

    const colorMap = {
      unassigned: "bg-gray-100 text-gray-700 border-gray-200",

      assigned: "bg-yellow-50 text-yellow-700 border-yellow-200",

      acknowledged: "bg-blue-50 text-blue-700 border-blue-200",

      arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",

      "food is ready": "bg-purple-50 text-purple-700 border-purple-200",

      "picked up": "bg-orange-50 text-orange-700 border-orange-200",

      successful: "bg-green-50 text-green-700 border-green-200",

      completed: "bg-green-50 text-green-700 border-green-200",

      cancelled: "bg-red-50 text-red-700 border-red-200",

      failed: "bg-red-50 text-red-700 border-red-200",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
          colorMap[value] || "bg-gray-100 text-gray-700 border-gray-200"
        }`}
      >
        <BadgeCheck size={13} />

        {status || "N/A"}
      </span>
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* =================================================
          SEARCH BAR
      ================================================= */}

      <div className="shrink-0 rounded-xl border-gray-200 p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          {/* SEARCH WORD */}

          <div className="w-full lg:max-w-[420px] lg:flex-1">
            <label className="mb-1 block text-xs font-semibold text-gray-700">
              Search
            </label>

            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400"
              />

              <input
                type="text"
                value={taskSearchWord}
                onChange={(event) => {
                  setTaskSearchWord(event.target.value);

                  if (!event.target.value.trim() && !fromDate && !toDate) {
                    setHasTaskSearched(false);
                    setTaskSearchResults([]);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
                placeholder="Task ID, order ID, mobile..."
                className="
          h-10 w-full
          rounded-lg
          border border-gray-200
          bg-gray-50
          pl-9 pr-3
          text-xs font-medium
          text-gray-800
          outline-none
          transition
          focus:border-orange-400
          focus:bg-white
          focus:ring-2
          focus:ring-orange-100
        "
              />
            </div>
          </div>

          {/* DATE FILTERS */}

          <div className="grid grid-cols-2 gap-2 sm:flex">
            {/* FROM */}

            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">
                From
              </label>

              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                max={toDate || undefined}
                className="
          h-10 w-full
          rounded-lg
          border border-gray-200
          bg-white
          px-3
          text-xs font-medium
          text-gray-800
          outline-none
          focus:border-orange-400
          focus:ring-2
          focus:ring-orange-100
          sm:w-[145px]
        "
              />
            </div>

            {/* TO */}

            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">
                To
              </label>

              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                min={fromDate || undefined}
                className="
          h-10 w-full
          rounded-lg
          border border-gray-200
          bg-white
          px-3
          text-xs font-medium
          text-gray-800
          outline-none
          focus:border-orange-400
          focus:ring-2
          focus:ring-orange-100
          sm:w-[145px]
        "
              />
            </div>
          </div>

          {/* SEARCH BUTTON */}

          <button
            type="button"
            onClick={handleSearch}
            disabled={!taskSearchWord.trim() || taskDetailsLoading}
            className="
      inline-flex h-10
      w-full
      items-center justify-center
      gap-1.5
      rounded-lg
      bg-orange-400
      px-5
      text-xs font-bold
      text-white
      shadow-sm
      transition
      hover:bg-orange-500
      disabled:cursor-not-allowed
      disabled:opacity-60
      sm:w-auto
    "
          >
            {taskDetailsLoading ? (
              <>
                <Loader size={14} className="animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search size={14} />
                Search
              </>
            )}
          </button>
        </div>
      </div>

      {/* =================================================
          LOADING
      ================================================= */}

      {taskDetailsLoading ? (
        <LoadingScreen />
      ) : hasTaskSearched && taskSearchResults.length > 0 ? (
        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-2">
          {taskSearchResults.map((item, index) => {
            const taskId =
              item?.pk_task_id ||
              item?.taskId ||
              item?.task_id ||
              item?.id ||
              taskSearchWord ||
              "N/A";

            const orderId = item?.order_id || item?.orderid || "N/A";

            const title =
              item?.task_description || item?.description || `Task #${taskId}`;

            const customerName =
              item?.customer_name ||
              item?.customerName ||
              item?.delivery_details?.customer_name ||
              "N/A";

            const phone =
              item?.contact_number ||
              item?.customer_mobile ||
              item?.phone ||
              item?.mobile ||
              item?.delivery_details?.customer_mobile ||
              "N/A";

            const status = item?.status || item?.task_status || "N/A";

            const merchantStatus = item?.merchant_order_status || "N/A";

            const driverName =
              item?.driver_name ||
              item?.driverName ||
              item?.delivery_person_name ||
              "N/A";

            const driverMobile = item?.driver_mobile || "N/A";

            const pickupLat = item?.pickup_latitude;

            const pickupLng = item?.pickup_longitude;

            const dropLat = item?.dropoff_latitude || item?.task_lat;

            const dropLng = item?.dropoff_longitude || item?.task_lng;

            const hasMapCoords =
              (pickupLat && pickupLng) || (dropLat && dropLng);

            return (
              <div
                key={item?.pk_task_id || item?.taskId || item?.id || index}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
              >
                {/* =====================================
                      HEADER
                  ===================================== */}

                <div className="border-b border-gray-100 bg-orange-50/70 px-4 py-3">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-orange-700">
                          <Hash size={12} />
                          Task #{taskId}
                        </span>

                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                          <ShoppingBag size={12} />
                          Order #{orderId}
                        </span>

                        {Number(item?.critical || 0) >= 2 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-700">
                            <ShieldAlert size={12} />
                            Critical {item?.critical}
                          </span>
                        )}
                      </div>

                      <h2 className="text-base font-bold text-gray-900 leading-snug">
                        {title}
                      </h2>

                      <p className="mt-0.5 text-[11px] text-gray-500">
                        Created: {formatDisplayDateTime(item?.date_created)}
                      </p>
                    </div>

                    {/* STATUS */}

                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={status} />

                      <StatusPill status={merchantStatus} />

                      <select
                        value={selectedSearchStatus || status?.toLowerCase()}
                        onChange={(event) =>
                          setSelectedSearchStatus(event.target.value)
                        }
                        className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-700 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                      >
                        {statusOptions.map((statusItem) => (
                          <option key={statusItem} value={statusItem}>
                            {statusItem.toUpperCase()}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => handleSearchStatusChange(item)}
                        disabled={searchStatusUpdating || !selectedSearchStatus}
                        className="inline-flex h-8 items-center justify-center rounded-lg bg-orange-400 px-3 text-xs font-bold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {searchStatusUpdating
                          ? "Updating..."
                          : selectedSearchStatus === "unassigned" &&
                              item?.status?.toLowerCase() !== "unassigned"
                            ? "Assign Driver"
                            : "Change Status"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-5 p-5">
                  {/* =====================================
                        MAP + SUMMARY
                    ===================================== */}

                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.15fr_0.85fr]">
                    {/* MAP */}

                    {hasMapCoords ? (
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-500">
                              <MapPinned size={15} />
                            </div>

                            <div>
                              <h3 className="text-xs font-bold text-gray-900">
                                Route Map
                              </h3>

                              <p className="text-[11px] text-gray-500">
                                Pickup and delivery location
                              </p>
                            </div>
                          </div>

                          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
                            {item?.taskDistance || "N/A"} •{" "}
                            {item?.taskEstimatedTime || "N/A"}
                          </span>
                        </div>

                        <div className="h-[360px]">
                          <MapContainer
                            center={[
                              Number(dropLat || pickupLat),

                              Number(dropLng || pickupLng),
                            ]}
                            zoom={14}
                            scrollWheelZoom={false}
                            style={{
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                            {pickupLat && pickupLng && (
                              <Marker
                                position={[
                                  Number(pickupLat),

                                  Number(pickupLng),
                                ]}
                              >
                                <Popup>Pickup Location</Popup>
                              </Marker>
                            )}

                            {dropLat && dropLng && (
                              <Marker
                                position={[Number(dropLat), Number(dropLng)]}
                              >
                                <Popup>Delivery Location</Popup>
                              </Marker>
                            )}
                          </MapContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
                        Map coordinates not available
                      </div>
                    )}

                    {/* SUMMARY */}

                    <div className="space-y-3">
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-500">
                            <ClipboardList size={15} />
                          </div>

                          <div>
                            <h3 className="text-xs font-bold text-gray-900">
                              Task Summary
                            </h3>

                            <p className="text-[11px] text-gray-500">
                              Customer and driver details
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                          <MiniInfo
                            icon={User}
                            label="Customer"
                            value={customerName}
                          />

                          <MiniInfo
                            icon={Phone}
                            label="Contact"
                            value={phone}
                          />

                          <MiniInfo
                            icon={Bike}
                            label="Driver"
                            value={driverName}
                          />

                          <MiniInfo
                            icon={Phone}
                            label="Driver Mobile"
                            value={driverMobile}
                          />

                          <MiniInfo
                            icon={Route}
                            label="Distance"
                            value={item?.taskDistance}
                          />

                          <MiniInfo
                            icon={Clock}
                            label="ETA"
                            value={item?.taskEstimatedTime}
                          />

                          <MiniInfo
                            icon={CalendarDays}
                            label="Delivery Date"
                            value={formatDisplayDateTime(item?.delivery_date)}
                          />

                          <MiniInfo
                            icon={CalendarClock}
                            label="Assigned Limit"
                            value={formatDisplayDateTime(
                              item?.assigned_limit_time,
                            )}
                          />
                        </div>
                      </div>

                      {/* PICKUP */}

                      <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                        <div className="mb-1.5 flex items-center gap-2">
                          <Store size={15} className="text-orange-500" />

                          <h3 className="text-xs font-bold text-gray-900">
                            Pickup Address
                          </h3>
                        </div>

                        <p className="line-clamp-3 text-xs font-semibold leading-5 text-gray-800">
                          {item?.pickup_address || "N/A"}
                        </p>
                      </div>

                      {/* DELIVERY */}

                      <div className="rounded-xl border border-green-100 bg-green-50/40 p-3">
                        <div className="mb-1.5 flex items-center gap-2">
                          <MapPinned size={15} className="text-green-600" />

                          <h3 className="text-xs font-bold text-gray-900">
                            Delivery Address
                          </h3>
                        </div>

                        <p className="line-clamp-3 text-xs font-semibold leading-5 text-gray-800">
                          {item?.delivery_address || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* =====================================
                        LOGS
                    ===================================== */}

                  <div className="rounded-2xl border border-gray-200 bg-white">
                    <div className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left bg-white">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                          <Activity size={17} />
                        </div>

                        <div>
                          <h3 className="text-sm font-bold text-gray-900">
                            Status & Assignment Logs
                          </h3>

                          <p className="text-xs text-gray-500">
                            Logs are automatically shown for this task
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 p-4">
                      {searchLogsLoading ? (
                        <div className="py-6 text-center text-sm font-medium text-gray-500">
                          Loading logs...
                        </div>
                      ) : searchStatusChangeLogs.length === 0 &&
                        searchAssignmentLogs.length === 0 ? (
                        <div className="py-6 text-center text-sm font-medium text-gray-500">
                          No logs available
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {/* STATUS LOGS */}

                          {searchStatusChangeLogs.length > 0 && (
                            <div className="rounded-xl border border-gray-200 p-4">
                              <h4 className="mb-5 text-xs font-bold uppercase tracking-wide text-gray-500">
                                Status Change Logs
                              </h4>

                              <div className="overflow-x-auto">
                                <div className="flex min-w-max items-start justify-center px-2">
                                  {searchStatusChangeLogs.map((log, idx) => {
                                    const logStatus =
                                      log?.status?.toLowerCase();

                                    const statusColorMap = {
                                      unassigned: "bg-gray-400",

                                      assigned: "bg-yellow-500",

                                      acknowledged: "bg-blue-500",

                                      arrived: "bg-green-500",

                                      "food is ready": "bg-purple-500",

                                      "picked up": "bg-orange-500",

                                      successful: "bg-green-700",

                                      completed: "bg-green-700",

                                      cancelled: "bg-red-500",

                                      failed: "bg-red-500",

                                      declined: "bg-pink-500",
                                    };

                                    const lineColorMap = {
                                      unassigned: "bg-gray-200",

                                      assigned: "bg-yellow-200",

                                      acknowledged: "bg-blue-200",

                                      arrived: "bg-green-200",

                                      "food is ready": "bg-purple-200",

                                      "picked up": "bg-orange-200",

                                      successful: "bg-green-200",

                                      completed: "bg-green-200",

                                      cancelled: "bg-red-200",

                                      failed: "bg-red-200",

                                      declined: "bg-pink-200",
                                    };

                                    const bgColor =
                                      statusColorMap[logStatus] ||
                                      "bg-blue-500";

                                    const lineColor =
                                      lineColorMap[logStatus] || "bg-blue-200";

                                    return (
                                      <React.Fragment key={idx}>
                                        <div className="flex min-w-[105px] flex-col items-center">
                                          <div
                                            className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm ring-4 ring-white ${bgColor}`}
                                          >
                                            {idx + 1}
                                          </div>

                                          <div className="mt-2 max-w-[95px] text-center text-[10px] font-bold uppercase leading-4 text-gray-700">
                                            {log?.status || "N/A"}
                                          </div>
                                        </div>

                                        {idx !==
                                          searchStatusChangeLogs.length - 1 && (
                                          <div
                                            className={`mt-5 h-[2px] w-12 ${lineColor}`}
                                          />
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* ASSIGNMENT LOGS */}

                          {searchAssignmentLogs.length > 0 && (
                            <div className="rounded-xl border border-gray-200 p-4">
                              <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
                                Assignment Logs
                              </h4>

                              <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="min-w-full text-xs">
                                  <thead className="bg-orange-50 text-orange-700">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-bold">
                                        Assign Type
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Driver ID
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Driver Name
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Task Status
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Status
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Assignment Date
                                      </th>

                                      <th className="px-3 py-2 text-left font-bold">
                                        Process Date
                                      </th>
                                    </tr>
                                  </thead>

                                  <tbody className="divide-y divide-gray-100 bg-white">
                                    {searchAssignmentLogs.map((log, idx) => (
                                      <tr
                                        key={idx}
                                        className="hover:bg-orange-50/40"
                                      >
                                        <td className="px-3 py-2 font-medium text-gray-800">
                                          {log?.assignType ||
                                            log?.assign_type ||
                                            "N/A"}
                                        </td>

                                        <td className="px-3 py-2">
                                          {log?.driverId ||
                                            log?.driver_id ||
                                            "N/A"}
                                        </td>

                                        <td className="px-3 py-2 font-semibold text-gray-900">
                                          {log?.driverName?.trim?.() ||
                                            log?.driver_name ||
                                            "N/A"}
                                        </td>

                                        <td className="px-3 py-2">
                                          {log?.taskStatus ||
                                            log?.task_status ||
                                            "N/A"}
                                        </td>

                                        <td className="px-3 py-2">
                                          <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-700">
                                            {log?.status || "N/A"}
                                          </span>
                                        </td>

                                        <td className="px-3 py-2">
                                          {log?.assignmentDate ||
                                            log?.assignment_date ||
                                            "N/A"}
                                        </td>

                                        <td className="px-3 py-2">
                                          {log?.processDate ||
                                            log?.process_date ||
                                            "N/A"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* =====================================
                        ORDER DETAILS
                    ===================================== */}

                  <div className="rounded-2xl border border-gray-200 bg-white p-4">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                        <ShoppingBag size={17} />
                      </div>

                      <div>
                        <h3 className="text-sm font-bold text-gray-900">
                          Order Details
                        </h3>

                        <p className="text-xs text-gray-500">
                          Merchant, order, assignment and verification
                          information
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <MiniInfo
                        icon={Hash}
                        label="Order ID"
                        value={item?.order_id}
                      />

                      <MiniInfo
                        icon={Store}
                        label="Merchant Order ID"
                        value={item?.merchant_order_id}
                      />

                      <MiniInfo
                        icon={BadgeCheck}
                        label="Merchant Status"
                        value={item?.merchant_order_status}
                      />

                      <MiniInfo
                        icon={Truck}
                        label="Transaction Type"
                        value={item?.trans_type}
                      />

                      <MiniInfo
                        icon={User}
                        label="User Type"
                        value={item?.user_type}
                      />

                      <MiniInfo
                        icon={Hash}
                        label="User ID"
                        value={item?.user_id}
                      />

                      <MiniInfo
                        icon={User}
                        label="Team ID"
                        value={item?.team_id}
                      />

                      <MiniInfo
                        icon={Bike}
                        label="Driver ID"
                        value={item?.driver_id}
                      />

                      <MiniInfo
                        icon={Clock}
                        label="Assign Started"
                        value={formatDisplayDateTime(item?.assign_started)}
                      />

                      <MiniInfo
                        icon={Clock}
                        label="Modified"
                        value={formatDisplayDateTime(item?.date_modified)}
                      />

                      <MiniInfo
                        icon={BadgeCheck}
                        label="Delivery Verified"
                        value={item?.delivery_verified}
                      />

                      <MiniInfo
                        icon={Hash}
                        label="Verification Code"
                        value={item?.delivery_verification_code}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : hasTaskSearched ? (
        /* =================================================
            NO RESULT
        ================================================= */

        <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-orange-200 bg-orange-50/40 text-center">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-orange-100 text-orange-500">
            <Search size={20} />
          </div>

          <h3 className="text-sm font-semibold text-gray-900">No data found</h3>

          <p className="mt-1 text-xs text-gray-500">
            No task details found for{" "}
            <span className="font-semibold text-orange-600">
              {taskSearchWord}
            </span>
          </p>
        </div>
      ) : (
        /* =================================================
            INITIAL STATE
        ================================================= */

        <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white text-center shadow-sm">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-orange-100 text-orange-500">
            <Search size={20} />
          </div>

          <h3 className="text-sm font-semibold text-gray-900">
            Search task details
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Enter task id and click search.
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskSearchTab;
